import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Activity, AlertCircle, Inbox as InboxIcon, Bot, ListTodo, Plug, SquareStack, Zap, type LucideIcon } from 'lucide-react';
import { ActivityFeed } from '@/components/activity-feed';
import { Page, PageHeader } from '@/components/page';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingPage } from '@/components/states';
import { WorkItemRow } from '@/components/work-item-row';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useDesks, useInbox, useMe, useWorkspace, useWorkspaceId } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

export function OverviewPage() {
  const workspaceId = useWorkspaceId();
  const overview = useQuery({
    queryKey: qk.overview(workspaceId),
    queryFn: () => api.workspaces.overview(workspaceId),
  });
  const desks = useDesks(workspaceId);
  const me = useMe();
  const workspaceInfo = useWorkspace(workspaceId);
  const inbox = useInbox(workspaceInfo.data?.accountId);
  const deskNames = new Map((desks.data ?? []).map((d) => [d.id, d.name]));

  if (overview.isPending) return <LoadingPage />;
  if (overview.isError) {
    return (
      <Page>
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      </Page>
    );
  }

  const { workspace, counts, attention, recentActivity } = overview.data;
  const account = me.data?.accounts.find((a) => a.id === workspace.accountId);
  const inboxLink = account ? account.slug || account.id : null;
  const inboxTotal = inbox.data?.total ?? 0;
  const p = { workspaceId };

  const stats: { label: string; value: number; icon: LucideIcon; to: string; hint: string; highlight?: boolean }[] = [
    { label: t('Needs attention'), value: counts.needsAttention, icon: AlertCircle, to: 'attention', hint: t('Approvals, questions, errors'), highlight: counts.needsAttention > 0 },
    { label: t('Open Work Items'), value: counts.openWorkItems, icon: ListTodo, to: 'open', hint: t('Not done yet') },
    { label: t('In progress'), value: counts.workingWorkItems, icon: Zap, to: 'working', hint: t('Agents are working on them') },
  ];

  return (
    <Page>
      <PageHeader
        title={workspace.name}
        description={workspace.description || t('See at a glance what’s happening in this Workspace and where you’re needed.')}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        {stats.map((s) => {
          const inner = (
            <Card
              className={cn(
                'h-full p-4 transition-colors',
                s.to !== 'none' && 'hover:border-primary/40',
                s.highlight && 'border-orange-300 bg-orange-50/60 dark:border-orange-900 dark:bg-orange-950/30',
              )}
            >
              <div className="flex items-center justify-between text-[13px] text-muted-foreground">
                <span>{s.label}</span>
                <s.icon className={cn('size-4', s.highlight && 'text-orange-600 dark:text-orange-400')} />
              </div>
              <div className="mt-2 text-2xl font-semibold tabular-nums">{s.value}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{s.hint}</div>
            </Card>
          );
          if (s.to === 'attention') return <a key={s.label} href="#attention">{inner}</a>;
          if (s.to === 'open')
            return (
              <Link key={s.label} to="/w/$workspaceId/work-items" params={p} search={{ status: 'open' }}>
                {inner}
              </Link>
            );
          if (s.to === 'working')
            return (
              <Link key={s.label} to="/w/$workspaceId/work-items" params={p} search={{ status: 'working' }}>
                {inner}
              </Link>
            );
          return <div key={s.label}>{inner}</div>;
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-[13px]">
        <SmallLink to="/w/$workspaceId/desks" params={p} icon={<SquareStack />}>
          {counts.desks === 1 ? t('1 Desk') : t('{n} Desks', { n: counts.desks })}
        </SmallLink>
        <SmallLink to="/w/$workspaceId/agents" params={p} icon={<Bot />}>
          {counts.agents === 1 ? t('1 Agent') : t('{n} Agents', { n: counts.agents })}
        </SmallLink>
        <SmallLink to="/w/$workspaceId/connections" params={p} icon={<Plug />}>
          {counts.connections === 1 ? t('1 Connection') : t('{n} Connections', { n: counts.connections })}
        </SmallLink>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Section
          id="attention"
          className="lg:col-span-3"
          icon={<AlertCircle />}
          title={t('Needs your attention')}
          description={t('Things waiting for you in this workspace: approvals, questions from agents, or failed work.')}
          actions={
            inboxLink && (
              <Button asChild size="xs" variant={inboxTotal > 0 ? 'default' : 'ghost'}>
                <Link to="/$account/inbox" params={{ account: inboxLink }}>
                  <InboxIcon /> {inboxTotal > 0 ? t('Open inbox ({n})', { n: inboxTotal }) : t('Open inbox')}
                </Link>
              </Button>
            )
          }
        >
          {inboxTotal > 0 && inboxLink && (
            <Link
              to="/$account/inbox"
              params={{ account: inboxLink }}
              className="mb-3 flex items-center gap-2 rounded-lg border border-orange-300 bg-orange-50/70 px-3 py-2 text-[13px] text-orange-900 hover:bg-orange-100/70 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-100"
            >
              <InboxIcon className="size-4 shrink-0" />
              <span className="flex-1">
                {inboxTotal === 1
                  ? t('1 thing needs you across all workspaces — answer questions, approve and retry right in the inbox.')
                  : t('{n} things need you across all workspaces — answer questions, approve and retry right in the inbox.', { n: inboxTotal })}
              </span>
              <span className="font-medium">{t('Open →')}</span>
            </Link>
          )}
          {attention.length === 0 ? (
            <EmptyState
              compact
              title={t('All caught up')}
              description={t('Nothing is waiting for you right now. Agents will show up here when they need you.')}
            />
          ) : (
            <div className="-mx-5 -mb-5 divide-y border-t">
              {attention.map((it) => (
                <WorkItemRow key={it.id} item={it} workspaceId={workspaceId} />
              ))}
            </div>
          )}
        </Section>

        <Section
          className="lg:col-span-2"
          icon={<Activity />}
          title={t('Recent activity')}
          actions={
            <Button asChild variant="ghost" size="xs">
              <Link to="/w/$workspaceId/activity" params={p}>
                {t('View all')}
              </Link>
            </Button>
          }
        >
          {recentActivity.length === 0 ? (
            <EmptyState compact title={t('No activity yet')} description={t('As soon as a Desk runs or an agent gets to work, you’ll see it here.')} />
          ) : (
            <ActivityFeed events={recentActivity.slice(0, 12)} workspaceId={workspaceId} deskNames={deskNames} compact />
          )}
        </Section>
      </div>

      {counts.desks === 0 && (
        <Card className="mt-6 p-6">
          <h2 className="text-base font-semibold">{t('Getting started')}</h2>
          <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
            <li>
              {tr('Create a runtime (which AI model to use) in {settings}.', {
                settings: <Link className="text-primary hover:underline" to="/w/$workspaceId/settings" params={p}>{t('Settings')}</Link>,
              })}
            </li>
            <li>
              {tr('Create an {agent} — an AI teammate with instructions.', {
                agent: <Link className="text-primary hover:underline" to="/w/$workspaceId/agents" params={p}>{t('Agent')}</Link>,
              })}
            </li>
            <li>
              {tr('Create a {desk} — an area of work that Deskworks takes care of for you — and pick the agent as its Lead Agent.', {
                desk: <Link className="text-primary hover:underline" to="/w/$workspaceId/desks" params={p}>{t('Desk')}</Link>,
              })}
            </li>
          </ol>
        </Card>
      )}
    </Page>
  );
}

function SmallLink({
  to,
  params,
  icon,
  children,
}: {
  to: '/w/$workspaceId/desks' | '/w/$workspaceId/agents' | '/w/$workspaceId/connections';
  params: { workspaceId: string };
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      params={params}
      className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1 text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
    >
      {icon}
      {children}
    </Link>
  );
}
